/**
 * @file catalog-status.ts
 * @description Подписи и тоны статусов зоны «Товары» (phase19.31; SC25–SC29):
 * товар на сайте, публикация категории, акция, производитель.
 *
 * Страница не выбирает цвет — берёт запись отсюда и отдаёт `StatusPill`.
 * Тоны по SC26: успех — зелёный, в работе — синий, черновик/архив/неактивно
 * — серый. Эмодзи в подписях нет (SC29).
 */

import type { CatalogCategory, CatalogProducer, CatalogProduct, CatalogPromotion } from "./api-catalog";
import type { ShopStatusMeta } from "./shop/status-meta";

// ─── Товар на сайте ─────────────────────────────────────────────────────

/**
 * Состояние товара для колонки «На сайте»:
 *  - `published` — товар активен и опубликован на сайте (канал «Сайт»);
 *  - `hidden` — активен, но на сайте не показывается;
 *  - `draft` — черновик (не продаётся нигде);
 *  - `archived` — снят с оборота.
 * Публикация на сайте — по каналу «Сайт» (`publishedChannels`), как у
 * витрины (catalog-taxonomy.md «Логика публикации»).
 */
export type ProductSiteState = "published" | "hidden" | "draft" | "archived";

export const PRODUCT_SITE_META: Record<ProductSiteState, ShopStatusMeta> = {
  published: { label: "Опубликован", tone: "success" },
  hidden: { label: "Скрыт", tone: "neutral" },
  draft: { label: "Черновик", tone: "neutral" },
  archived: { label: "В архиве", tone: "neutral" },
};

/** Подпись выбора в фильтре «Публикация». */
export const PRODUCT_SITE_FILTER_LABEL: Record<ProductSiteState, string> = {
  published: "Опубликованные",
  hidden: "Скрытые с сайта",
  draft: "Черновики",
  archived: "В архиве",
};

export function productSiteState(product: Pick<CatalogProduct, "status" | "publishedChannels">): ProductSiteState {
  if (product.status === "ARCHIVED") return "archived";
  if (product.status === "DRAFT") return "draft";
  return product.publishedChannels.includes("WEBSITE") ? "published" : "hidden";
}

/** Статус товара в карточке (шапка): по полю `status`. */
export const PRODUCT_STATUS_META: Record<CatalogProduct["status"], ShopStatusMeta> = {
  ACTIVE: { label: "Опубликован", tone: "success" },
  DRAFT: { label: "Черновик", tone: "neutral" },
  ARCHIVED: { label: "В архиве", tone: "neutral" },
};

// ─── Публикация категории ───────────────────────────────────────────────

/** Колонка «На сайте» в дереве категорий — словами (SC52). */
export type CategoryPublishMode = "yes" | "inherit" | "no";

export const CATEGORY_PUBLISH_LABEL: Record<CategoryPublishMode, string> = {
  yes: "да",
  inherit: "по родителю",
  no: "нет",
};

/** Пункты подменю «Публикация» в «⋯» строки категории. */
export const CATEGORY_PUBLISH_ACTION_LABEL: Record<CategoryPublishMode, string> = {
  yes: "Показывать на сайте",
  inherit: "Как у родителя",
  no: "Не показывать на сайте",
};

export function categoryPublishMode(category: Pick<CatalogCategory, "isPublishedOnSite">): CategoryPublishMode {
  if (category.isPublishedOnSite === null) return "inherit";
  return category.isPublishedOnSite ? "yes" : "no";
}

export const CATEGORY_PUBLISH_VALUE: Record<CategoryPublishMode, boolean | null> = {
  yes: true,
  inherit: null,
  no: false,
};

// ─── Акции ──────────────────────────────────────────────────────────────

/** Состояние акции: идёт, запланирована, завершена, выключена. */
export type PromotionState = "running" | "scheduled" | "finished" | "off";

export const PROMOTION_STATE_META: Record<PromotionState, ShopStatusMeta> = {
  running: { label: "Идёт", tone: "success" },
  scheduled: { label: "Запланирована", tone: "progress" },
  finished: { label: "Завершена", tone: "neutral" },
  off: { label: "Выключена", tone: "neutral" },
};

/** «Идёт» считает сервер (`isRunning`), остальное — по датам. */
export function promotionState(
  promotion: Pick<CatalogPromotion, "isActive" | "isRunning" | "startsAt" | "endsAt">,
  now: Date = new Date(),
): PromotionState {
  if (promotion.isRunning) return "running";
  if (promotion.endsAt && new Date(promotion.endsAt) < now) return "finished";
  if (!promotion.isActive) return "off";
  return new Date(promotion.startsAt) > now ? "scheduled" : "finished";
}

// ─── Производители и поставщики ─────────────────────────────────────────

/** У производителя и поставщика флага публикации нет — только «включён». */
export const DIRECTORY_ACTIVE_META: Record<"active" | "inactive", ShopStatusMeta> = {
  active: { label: "Активен", tone: "success" },
  inactive: { label: "Отключён", tone: "neutral" },
};

export function directoryActiveMeta(item: Pick<CatalogProducer, "isActive">): ShopStatusMeta {
  return DIRECTORY_ACTIVE_META[item.isActive ? "active" : "inactive"];
}
